import { EyeHideIcon, EyeOpenIcon } from '@/src/assets/icons'
import { IconButton, InputAdornment, TextField, LinearProgress, Typography } from '@mui/material'
import React, { useState } from 'react'
import { InputLabelProps } from '@mui/material/InputLabel'

interface FieldProps {
  variant?: 'filled' | 'outlined' | 'standard'
  label?: string
  passwordKey?: string
  defaultValue?: string | number
  placeholder?: string
  error?: boolean
  helperText?: string
  fieldClass?: string
  divClass?: string
  id?: string
  validation?: {
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    [key: string]: any
  }
  props?: Record<string, any>
  InputLabelProps?: InputLabelProps
}

interface PasswordRequirements {
  hasMinLength: boolean
  hasUppercase: boolean
  hasNumber: boolean
  hasSpecialChar: boolean
}

const PasswordField = ({
  label = '',
  defaultValue = '',
  fieldClass = '',
  divClass = '',
  placeholder,
  validation = {},
  helperText,
  error,
  variant = 'outlined',
  id = '',
  passwordKey = 'login',
  ...props
}: FieldProps) => {
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [password, setPassword] = useState<string>(defaultValue.toString())
  const [strength, setStrength] = useState<number>(0)
  const [strengthText, setStrengthText] = useState<string>('')
  const [requirements, setRequirements] = useState<PasswordRequirements>({
    hasMinLength: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false
  })

  const handleTogglePassword = () => {
    setShowPassword(prevShowPassword => !prevShowPassword)
  }

  const checkPasswordRequirements = (value: string): PasswordRequirements => {
    return {
      hasMinLength: value.length >= 8,
      hasUppercase: /[A-Z]/.test(value),
      hasNumber: /[0-9]/.test(value),
      hasSpecialChar: /[^A-Za-z0-9]/.test(value)
    }
  }

  const checkPasswordStrength = (value: string) => {
    // Check specific requirements
    const reqs = checkPasswordRequirements(value)
    setRequirements(reqs)

    // Start with a base score of 0
    let score = 0

    // Add points for length
    if (value.length > 0) score += 1
    if (reqs.hasMinLength) score += 2 // More weight to having 8+ chars

    // Add points for complexity
    if (reqs.hasUppercase) score += 1
    if (reqs.hasNumber) score += 1
    if (reqs.hasSpecialChar) score += 1

    // Add more points for extra complexity
    if (value.length >= 12) score += 1
    if (/[a-z]/.test(value)) score += 1 // lowercase

    // Determine strength level
    let strengthLevel = 0
    let text = ''

    // Pass all required criteria
    const meetsAllRequirements =
      reqs.hasMinLength && reqs.hasUppercase && reqs.hasNumber && reqs.hasSpecialChar

    if (value.length === 0) {
      strengthLevel = 0
      text = ''
    } else if (score <= 3) {
      strengthLevel = 25
      text = 'Weak'
    } else if (score <= 5) {
      strengthLevel = 50
      text = 'Medium'
    } else if (score <= 7) {
      strengthLevel = 75
      text = 'Strong'
    } else {
      strengthLevel = 100
      text = 'Very Strong'
    }

    // If we don't meet all required criteria, cap at medium
    if (!meetsAllRequirements && strengthLevel > 50) {
      strengthLevel = 50
      text = 'Medium'
    }

    setStrength(strengthLevel)
    setStrengthText(text)
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setPassword(newValue)
    checkPasswordStrength(newValue)

    // Call the validation onChange if it exists
    if (validation?.onChange) {
      validation.onChange(e)
    }
  }

  const getStrengthColor = () => {
    if (strength <= 25) return 'error'
    if (strength <= 50) return 'warning'
    return 'success'
  }

  return (
    <div className={divClass}>
      <TextField
        id={id}
        label={label}
        variant={variant}
        autoComplete="off"
        placeholder={placeholder}
        {...validation}
        onChange={handlePasswordChange}
        value={password}
        error={error}
        helperText={helperText}
        type={showPassword ? 'text' : 'password'}
        fullWidth
        InputProps={{
          className: `rounded-[0.625rem] border-icon-darkgray !h-[3.375rem] ${fieldClass} `,
          endAdornment: (
            <InputAdornment className="!py-[0px] rounded-[10px]" position="end">
              <IconButton
                className="m-0 rounded-[0.625rem]]"
                onClick={handleTogglePassword}
                size="small"
              >
                {showPassword ? (
                  <EyeOpenIcon className="w-[1.375rem] h-4 fill-none" />
                ) : (
                  <EyeHideIcon className="w-[1.313rem] h-[1.188rem] fill-secondary-darkGray" />
                )}
              </IconButton>
            </InputAdornment>
          )
        }}
        {...props}
      />

      {password.length > 0 && passwordKey != 'login' && (
        <div className="mt-2">
          <LinearProgress
            variant="determinate"
            value={strength}
            color={getStrengthColor()}
            sx={{
              height: 5,
              borderRadius: 3,
              marginBottom: 1
            }}
          />
          <Typography
            variant="inputHelper"
            color="text.secondary"
            className={`col-span-full font-normal text-${getStrengthColor()}-main`}
          >
            {strengthText && `Password strength: ${strengthText}`}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default PasswordField
